import { useEffect, useState } from 'react'
import { useParams, useOutletContext } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  getOrgUsers,
  createOrgUser,
  changeOrgUserRole,
  toggleOrgUserDisable,
  type AdminOrgUser,
} from '../lib/api'
import type { TenantDetailContext } from './TenantDetailLayout'

export default function TenantUsersPage() {
  const { id } = useParams<{ id: string }>()
  const { token } = useAuth()
  useOutletContext<TenantDetailContext>()
  const [users, setUsers] = useState<AdminOrgUser[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [addRole, setAddRole] = useState<'manager' | 'operator'>('manager')
  const [addError, setAddError] = useState('')
  const [addSaving, setAddSaving] = useState(false)
  const [rowError, setRowError] = useState<Record<string, string>>({})

  async function load() {
    if (!id || !token) return
    setLoading(true)
    try {
      setUsers(await getOrgUsers(token, id))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [id, token])

  async function handleAddUser(e: React.FormEvent) {
    e.preventDefault()
    if (!token || !id) return
    setAddError('')
    setAddSaving(true)
    try {
      await createOrgUser(token, id, { email: addEmail, password: addPassword, role: addRole })
      setAddEmail('')
      setAddPassword('')
      setAddRole('manager')
      setShowAddForm(false)
      await load()
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Gagal menambah user.')
    } finally {
      setAddSaving(false)
    }
  }

  async function handleRoleChange(uid: string, currentRole: 'manager' | 'operator') {
    if (!token || !id) return
    const newRole = currentRole === 'manager' ? 'operator' : 'manager'
    try {
      await changeOrgUserRole(token, id, uid, newRole)
      setUsers(prev => prev.map(u => u.user_id === uid ? { ...u, role: newRole } : u))
      setRowError(prev => ({ ...prev, [uid]: '' }))
    } catch (err) {
      setRowError(prev => ({ ...prev, [uid]: err instanceof Error ? err.message : 'Gagal.' }))
    }
  }

  async function handleToggleDisable(uid: string, isDisabled: boolean) {
    if (!token || !id) return
    try {
      await toggleOrgUserDisable(token, id, uid, !isDisabled)
      setUsers(prev => prev.map(u => u.user_id === uid ? { ...u, is_disabled: !isDisabled } : u))
      setRowError(prev => ({ ...prev, [uid]: '' }))
    } catch (err) {
      setRowError(prev => ({ ...prev, [uid]: err instanceof Error ? err.message : 'Gagal.' }))
    }
  }

  const input = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'

  return (
    <div className="p-6 max-w-3xl space-y-4">
      {/* Header + add toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white">Organizer Accounts</h2>
        <button
          onClick={() => { setShowAddForm(v => !v); setAddError('') }}
          className="rounded bg-yellow-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-yellow-400 transition-colors"
        >
          {showAddForm ? 'Batal' : '+ Tambah User'}
        </button>
      </div>

      {/* Add user form */}
      {showAddForm && (
        <form onSubmit={handleAddUser} className="rounded-xl border border-zinc-700 bg-zinc-900 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Email</label>
              <input
                type="email"
                className={input}
                value={addEmail}
                onChange={e => setAddEmail(e.target.value)}
                placeholder="organizer@email.com"
                required
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Password sementara</label>
              <input
                type="password"
                className={input}
                value={addPassword}
                onChange={e => setAddPassword(e.target.value)}
                required
                minLength={8}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Role</label>
            <select
              className={input}
              value={addRole}
              onChange={e => setAddRole(e.target.value as 'manager' | 'operator')}
            >
              <option value="manager">Manager</option>
              <option value="operator">Operator</option>
            </select>
          </div>
          {addError && <p className="text-xs text-red-400">{addError}</p>}
          <button
            type="submit"
            disabled={addSaving}
            className="rounded bg-yellow-500 px-4 py-2 text-xs font-bold text-black hover:bg-yellow-400 disabled:opacity-50 transition-colors"
          >
            {addSaving ? 'Menyimpan…' : 'Simpan'}
          </button>
        </form>
      )}

      {/* User list */}
      {loading ? (
        <p className="text-zinc-500 text-sm">Loading…</p>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-8 text-center">
          <p className="text-zinc-400 text-sm">Belum ada user. Tambah organizer pertama.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 divide-y divide-zinc-800 overflow-hidden">
          {users.map(u => (
            <div key={u.user_id} className="px-5 py-4 flex items-center gap-4">
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-xs font-medium text-zinc-300 shrink-0 select-none">
                {u.email.slice(0, 2).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{u.email}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                    u.role === 'manager'
                      ? 'bg-blue-900/40 text-blue-400'
                      : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {u.role}
                  </span>
                  {u.is_disabled && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-900/40 text-red-400">
                      Disabled
                    </span>
                  )}
                </div>
                {rowError[u.user_id] && (
                  <p className="text-xs text-red-400 mt-1">{rowError[u.user_id]}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleRoleChange(u.user_id, u.role)}
                  className="text-xs text-zinc-400 hover:text-white border border-zinc-700 px-3 py-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
                >
                  → {u.role === 'manager' ? 'Operator' : 'Manager'}
                </button>
                <button
                  onClick={() => handleToggleDisable(u.user_id, u.is_disabled)}
                  className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                    u.is_disabled
                      ? 'text-green-400 border-green-900 hover:bg-green-950'
                      : 'text-red-400 border-red-900 hover:bg-red-950'
                  }`}
                >
                  {u.is_disabled ? 'Enable' : 'Disable'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
